import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';

export interface TenantBranding {
  logoUrl?: string | null;
  primaryColor?: string | null;
  updatedAt?: string;
}

export interface TenantConfigResponse {
  tenantId: string | null;
  appName: string;
  customDomain?: string | null;
  workspaceSlug?: string | null;
  subdomain?: string | null;
  branding?: TenantBranding | null;
}

@Injectable({
  providedIn: 'root',
})
export class TenantBrandingService {
  private http = inject(HttpClient);
  private envService = inject(EnvService);
  private titleService = inject(Title);

  readonly tenantConfig = signal<TenantConfigResponse | null>(null);

  readonly logoUrl = computed(() => this.tenantConfig()?.branding?.logoUrl || null);
  readonly primaryColor = computed(() => this.tenantConfig()?.branding?.primaryColor || null);
  readonly appName = computed(() => this.tenantConfig()?.appName || 'SkillzTank');
  readonly customDomain = computed(() => this.tenantConfig()?.customDomain || null);
  readonly workspaceSlug = computed(() => this.tenantConfig()?.workspaceSlug || null);
  readonly subdomain = computed(() => this.tenantConfig()?.subdomain || null);

  readonly isWorkspaceNotFound = computed(() => {
    const config = this.tenantConfig();
    if (!config) return false;
    if (config.tenantId) return false;
    const host = window.location.hostname.toLowerCase();
    const isMainDomain = host === 'localhost' ||
                         host === '127.0.0.1' ||
                         host === 'app.skillztank.com' ||
                         host === 'dev.skillztank.com' ||
                         host === 'skillztank.com' ||
                         host === 'skillztank-k8s.com';
    return !isMainDomain;
  });

  private get apiUrl(): string {
    const base = (this.envService.apiUrl || '').replace(/\/+$/, '');
    return base;
  }

  async loadTenantConfig(): Promise<TenantConfigResponse | null> {
    try {
      const hostname = window.location.hostname;
      const activeWorkspace = localStorage.getItem('active_workspace') || (this.envService as any).appName || '';
      let url = `${this.apiUrl}/public/tenant-config?domain=${encodeURIComponent(hostname)}`;
      if (activeWorkspace) {
        url += `&tenantId=${encodeURIComponent(activeWorkspace)}`;
      }
      const res = await firstValueFrom(this.http.get<TenantConfigResponse>(url));
      this.tenantConfig.set(res);

      if (res?.branding?.logoUrl) {
        localStorage.setItem('tenant_branding_logo', res.branding.logoUrl);
      } else {
        localStorage.removeItem('tenant_branding_logo');
      }

      if (res?.appName) {
        this.titleService.setTitle(res.appName);
        document.title = res.appName;
      }

      if (res?.branding?.primaryColor) {
        this.applyPrimaryColor(res.branding.primaryColor);
      }
      return res;
    } catch (err) {
      console.warn('[TenantBrandingService] Failed to load tenant branding config:', err);
      return null;
    }
  }

  applyPrimaryColor(color?: string | null): void {
    if (!color) {
      document.documentElement.style.removeProperty('--skz-primary-color');
      document.documentElement.style.removeProperty('--skz-background-content-button');
      return;
    }
    document.documentElement.style.setProperty('--skz-primary-color', color);
    document.documentElement.style.setProperty('--skz-background-content-button', color);
  }

  uploadLogo(file: File): Observable<{ success: boolean; logoUrl: string }> {
    const formData = new FormData();
    formData.append('logo', file);

    return this.http.post<{ success: boolean; logoUrl: string }>(`${this.apiUrl}/tenant/branding/upload-logo`, formData).pipe(
      tap((res) => {
        if (res.success && res.logoUrl) {
          localStorage.setItem('tenant_branding_logo', res.logoUrl);
          const current = this.tenantConfig();
          if (current) {
            this.tenantConfig.set({
              ...current,
              branding: {
                ...(current.branding || {}),
                logoUrl: res.logoUrl,
              },
            });
          }
        }
      })
    );
  }

  updateSettings(primaryColor: string | null): Observable<{ success: boolean; primaryColor: string | null }> {
    return this.http
      .post<{ success: boolean; primaryColor: string | null }>(`${this.apiUrl}/tenant/branding/settings`, { primaryColor })
      .pipe(
        tap((res) => {
          if (res.success) {
            const current = this.tenantConfig();
            if (current) {
              this.tenantConfig.set({
                ...current,
                branding: {
                  ...(current.branding || {}),
                  primaryColor: res.primaryColor,
                },
              });
            }
            this.applyPrimaryColor(res.primaryColor);
          }
        })
      );
  }

  deleteLogo(): Observable<{ success: boolean; logoUrl: null }> {
    return this.http.delete<{ success: boolean; logoUrl: null }>(`${this.apiUrl}/tenant/branding/logo`).pipe(
      tap((res) => {
        if (res.success) {
          localStorage.removeItem('tenant_branding_logo');
          const current = this.tenantConfig();
          if (current) {
            this.tenantConfig.set({
              ...current,
              branding: {
                ...(current.branding || {}),
                logoUrl: null,
              },
            });
          }
        }
      })
    );
  }
}
